import { useState, useEffect } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import './Settings.css';

function IconPencil() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 20h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path
        d="M16.5 3.5a2.12 2.12 0 0 1 3 3L8 18l-4 1 1-4L16.5 3.5Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconDownload() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 4v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="m8 12 4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 20h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconUpload() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 16V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="m8 11 4-4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 20h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconPeople() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const isHr = user?.role === 'HR Manager';
  const [tab, setTab] = useState(isHr ? 'csv' : 'provider');
  const [provider, setProvider] = useState({ provider_name: '', provider_country: '', provider_pan: '', logo_url: '' });
  const [signatories, setSignatories] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [keyFieldGroups, setKeyFieldGroups] = useState([]);
  const [hrsReasons, setHrsReasons] = useState([]);
  const [forexGrid, setForexGrid] = useState({ base: 'INR', fromDate: '', toDate: '', dates: [], ccys: [], cells: {} });
  const [dashboards, setDashboards] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  // Modal states
  const [sigModal, setSigModal] = useState(null);
  const [addrModal, setAddrModal] = useState(null);
  const [currModal, setCurrModal] = useState(null);
  const [cgModal, setCgModal] = useState(null);
  const [kfgModal, setKfgModal] = useState(null);
  const [hrsModal, setHrsModal] = useState(null);
  const [dashModal, setDashModal] = useState(null);
  const [dashAccessModal, setDashAccessModal] = useState(null); // { dashboard, userIds: Set }

  useEffect(() => {
    api.get('/settings/provider').then(p => { if (p && p.provider_name) setProvider(p); });
    loadAll();
  }, []);

  const loadAll = () => {
    api.get('/settings/signatories').then(setSignatories);
    api.get('/settings/addresses').then(setAddresses);
    api.get('/settings/currencies').then(setCurrencies);
    api.get('/settings/customer-groups').then(setCustomerGroups);
    api.get('/settings/key-field-groups').then(setKeyFieldGroups);
    api.get('/settings/hrs-change-reasons').then(setHrsReasons).catch(() => {});
    api.get('/dashboards/catalog').then(setDashboards).catch(() => {});
    api.get('/auth/users').then(setAllUsers).catch(() => {});
  };

  // Dashboards (catalog)
  const saveDash = async () => {
    try {
      if (dashModal.id) await api.put(`/dashboards/${dashModal.id}`, dashModal);
      else await api.post('/dashboards', dashModal);
      setDashModal(null); api.get('/dashboards/catalog').then(setDashboards);
    } catch (e) { alert(e.message); }
  };

  // Dashboards (per-user access)
  const openDashAccess = async (d) => {
    const access = await api.get(`/dashboards/${d.id}/access`);
    setDashAccessModal({ dashboard: d, userIds: new Set(access.map(a => a.user_id)) });
  };
  const toggleDashUser = (uid) => {
    setDashAccessModal(m => {
      const s = new Set(m.userIds);
      if (s.has(uid)) s.delete(uid); else s.add(uid);
      return { ...m, userIds: s };
    });
  };
  const saveDashAccess = async () => {
    try {
      await api.put(`/dashboards/${dashAccessModal.dashboard.id}/access`, { user_ids: [...dashAccessModal.userIds] });
      setDashAccessModal(null);
      alert('Dashboard access saved!');
    } catch (e) { alert(e.message); }
  };

  const generateForexGrid = async () => {
    const { fromDate, toDate, base } = forexGrid;
    if (!fromDate || !toDate) { alert('Pick From and To date'); return; }
    if (fromDate > toDate) { alert('From date must be before To date'); return; }
    const dates = [];
    const d = new Date(fromDate); const end = new Date(toDate);
    while (d <= end) { dates.push(d.toISOString().slice(0, 10)); d.setDate(d.getDate() + 1); }
    if (dates.length > 120) { if (!window.confirm(`This will generate ${dates.length} rows. Continue?`)) return; }
    const ccys = currencies.filter(c => c.is_active && c.code !== base).map(c => c.code);
    const params = new URLSearchParams({ from_date: fromDate, to_date: toDate, from_currency: base });
    const existing = await api.get(`/settings/forex?${params}`);
    const cells = {};
    existing.forEach(r => { cells[`${r.rate_date}|${r.to_currency}`] = String(r.rate); });
    setForexGrid(g => ({ ...g, dates, ccys, cells }));
  };

  const updateForexCell = (date, ccy, value) => {
    setForexGrid(g => ({ ...g, cells: { ...g.cells, [`${date}|${ccy}`]: value } }));
  };

  const saveForexGrid = async () => {
    const rows = forexGrid.dates.map(rate_date => {
      const rates = {};
      forexGrid.ccys.forEach(ccy => {
        const v = forexGrid.cells[`${rate_date}|${ccy}`];
        if (v !== undefined && v !== '') rates[ccy] = v;
      });
      return { rate_date, rates };
    }).filter(r => Object.keys(r.rates).length > 0);
    if (rows.length === 0) { alert('Nothing to save'); return; }
    try { await api.post('/settings/forex/bulk', { base: forexGrid.base, rows }); alert('Forex rates saved!'); }
    catch (e) { alert(e.message); }
  };

  const saveProvider = async () => { try { await api.put('/settings/provider', provider); alert('Saved!'); } catch (e) { alert(e.message); } };

  // Signatories
  const saveSig = async () => {
    try {
      if (sigModal.id) await api.put(`/settings/signatories/${sigModal.id}`, sigModal);
      else await api.post('/settings/signatories', sigModal);
      setSigModal(null); api.get('/settings/signatories').then(setSignatories);
    } catch (e) { alert(e.message); }
  };

  // Addresses
  const saveAddr = async () => {
    try {
      if (addrModal.id) await api.put(`/settings/addresses/${addrModal.id}`, addrModal);
      else await api.post('/settings/addresses', addrModal);
      setAddrModal(null); api.get('/settings/addresses').then(setAddresses);
    } catch (e) { alert(e.message); }
  };

  // Currencies
  const saveCurr = async () => {
    try {
      if (currModal._editing) await api.put(`/settings/currencies/${currModal.code}`, currModal);
      else await api.post('/settings/currencies', currModal);
      setCurrModal(null); api.get('/settings/currencies').then(setCurrencies);
    } catch (e) { alert(e.message); }
  };

  // Customer Groups
  const saveCg = async () => {
    try {
      if (cgModal.id) await api.put(`/settings/customer-groups/${cgModal.id}`, cgModal);
      else await api.post('/settings/customer-groups', cgModal);
      setCgModal(null); api.get('/settings/customer-groups').then(setCustomerGroups);
    } catch (e) { alert(e.message); }
  };

  // Hrs Change Reasons
  const saveHrs = async () => {
    try {
      if (hrsModal.id) await api.put(`/settings/hrs-change-reasons/${hrsModal.id}`, hrsModal);
      else await api.post('/settings/hrs-change-reasons', hrsModal);
      setHrsModal(null); api.get('/settings/hrs-change-reasons').then(setHrsReasons);
    } catch (e) { alert(e.message); }
  };

  // Key Field Groups
  const saveKfg = async () => {
    try {
      if (kfgModal.id) await api.put(`/settings/key-field-groups/${kfgModal.id}`, kfgModal);
      else await api.post('/settings/key-field-groups', kfgModal);
      setKfgModal(null); api.get('/settings/key-field-groups').then(setKeyFieldGroups);
    } catch (e) { alert(e.message); }
  };

  const tabs = [
    { key: 'provider', label: 'Provider' },
    { key: 'signatories', label: 'Signatories' },
    { key: 'addresses', label: 'Addresses' },
    { key: 'currencies', label: 'Currencies' },
    { key: 'forex', label: 'Forex Rates' },
    { key: 'groups', label: 'Groups' },
    { key: 'dashboards', label: 'Dashboards' },
    { key: 'csv', label: 'CSV Import' }
  ];

  const allCsvEntities = [
    { key: 'customers', label: 'Customer Details', note: 'Inserts new customers; existing IDs are updated.' },
    { key: 'msa', label: 'MSA', note: 'Auto-generates MSA IDs unless provided.' },
    { key: 'sow', label: 'SOW', note: 'Linked to a parent MSA — msa_id required.' },
    { key: 'clauses_msa', label: 'Clause Library (MSA)', note: 'Templates by clause title.' },
    { key: 'clauses_sow', label: 'Clause Library (SOW)', note: 'Templates by clause title.' },
    { key: 'employees', label: 'Employee Master', note: 'Duplicate emp_ids are rejected (no overwrite). Required: emp_id, full_name.' }
  ];
  const csvEntities = isHr ? allCsvEntities.filter(e => e.key === 'employees') : allCsvEntities;

  const downloadTemplate = (entity) => {
    const token = localStorage.getItem('token');
    fetch(`${window.location.origin}/api/csv-import/template/${entity}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `${entity}_template.csv`; a.click();
        URL.revokeObjectURL(url);
      });
  };

  const uploadCsv = async (entity, file) => {
    if (!file) return;
    const text = await file.text();
    try {
      const res = await api.post(`/csv-import/upload/${entity}`, { csv: text });
      let msg = `${entity}: inserted ${res.inserted}, updated ${res.updated || 0}` + (res.skipped != null ? `, skipped ${res.skipped}` : '') + '.';
      if (res.errors?.length) msg += `\n\nErrors:\n${res.errors.join('\n')}`;
      alert(msg);
    } catch (e) { alert(e.message); }
  };

  return (
    <>
      <div className="page-header">
        <h1>Settings</h1>
      </div>
      <div className="page-content settings-page clause-library-page">
        <div className="clause-lib-tab-strip" role="tablist" aria-label="Settings sections">
          {tabs.filter(t => !isHr || t.key === 'csv').map(t => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={`clause-lib-tab ${tab === t.key ? 'clause-lib-tab-active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'provider' && (
          <div className="card">
            <div className="card-header">Service provider details</div>
            <p className="settings-hint-muted settings-intro">
              Company identity used on agreements and invoices. GST is stored on each address.
            </p>
            <div className="form-grid">
              <div className="form-group"><label>Company name</label><input value={provider.provider_name} onChange={e => setProvider(f => ({ ...f, provider_name: e.target.value }))} /></div>
              <div className="form-group"><label>Country</label><input value={provider.provider_country || ''} onChange={e => setProvider(f => ({ ...f, provider_country: e.target.value }))} /></div>
              <div className="form-group"><label>PAN</label><input value={provider.provider_pan || ''} onChange={e => setProvider(f => ({ ...f, provider_pan: e.target.value }))} /></div>
              <div className="form-group full-width">
                <p className="settings-hint-block">GST is maintained per address. Use the <strong>Addresses</strong> tab to add or edit GST for each location.</p>
              </div>
            </div>
            <div className="mt-4">
              <button type="button" className="btn btn-primary" onClick={saveProvider}>Save provider settings</button>
            </div>
          </div>
        )}

        {tab === 'signatories' && (
          <div className="card">
            <div className="card-header settings-card-header">
              <span>Authorized signatories</span>
              <button type="button" className="btn btn-primary btn-sm" onClick={() => setSigModal({ name: '', title: '', email: '', is_active: 1 })}>+ Add signatory</button>
            </div>
            <div className="settings-table-wrap">
              <table>
                <thead><tr><th>Name</th><th>Title</th><th>Email</th><th>Active</th><th>Actions</th></tr></thead>
                <tbody>
                  {signatories.map(s => (
                    <tr key={s.id}>
                      <td className="settings-cell-strong">{s.name}</td>
                      <td>{s.title}</td>
                      <td>{s.email}</td>
                      <td><span className={`badge ${s.is_active ? 'badge-approved' : 'badge-draft'}`}>{s.is_active ? 'Active' : 'Inactive'}</span></td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-outline btn-sm settings-icon-btn"
                          onClick={() => setSigModal({ ...s })}
                          title="Edit signatory"
                          aria-label={`Edit signatory ${s.name}`}
                        >
                          <IconPencil />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'addresses' && (
          <div className="card">
            <div className="card-header settings-card-header">
              <span>Provider addresses</span>
              <button type="button" className="btn btn-primary btn-sm" onClick={() => setAddrModal({ address_title: '', address_line: '', city: '', state: '', country: '', pincode: '', gst: '', is_default: 0, is_active: 1 })}>+ Add address</button>
            </div>
            <div className="settings-table-wrap">
              <table>
                <thead><tr><th>Title</th><th>Address</th><th>City</th><th>State</th><th>GST</th><th>Default</th><th>Active</th><th>Actions</th></tr></thead>
                <tbody>
                  {addresses.map(a => (
                    <tr key={a.id}>
                      <td className="settings-cell-strong">{a.address_title}</td>
                      <td>{a.address_line}</td>
                      <td>{a.city}</td>
                      <td>{a.state}</td>
                      <td className="settings-cell-mono">{a.gst || '—'}</td>
                      <td>{a.is_default ? '★' : ''}</td>
                      <td><span className={`badge ${a.is_active ? 'badge-approved' : 'badge-draft'}`}>{a.is_active ? 'Active' : 'Inactive'}</span></td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-outline btn-sm settings-icon-btn"
                          onClick={() => setAddrModal({ ...a })}
                          title="Edit address"
                          aria-label={`Edit address ${a.address_title}`}
                        >
                          <IconPencil />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'currencies' && (
          <div className="card">
            <div className="card-header settings-card-header">
              <span>Currency master</span>
              <button type="button" className="btn btn-primary btn-sm" onClick={() => setCurrModal({ code: '', name: '', symbol: '', is_active: 1 })}>+ Add currency</button>
            </div>
            <div className="settings-table-wrap">
              <table>
                <thead><tr><th>Code</th><th>Name</th><th>Symbol</th><th>Active</th><th>Actions</th></tr></thead>
                <tbody>
                  {currencies.map(c => (
                    <tr key={c.code}>
                      <td className="settings-cell-strong">{c.code}</td>
                      <td>{c.name}</td>
                      <td>{c.symbol}</td>
                      <td><span className={`badge ${c.is_active ? 'badge-approved' : 'badge-draft'}`}>{c.is_active ? 'Active' : 'Inactive'}</span></td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-outline btn-sm settings-icon-btn"
                          onClick={() => setCurrModal({ ...c, _editing: true })}
                          title="Edit currency"
                          aria-label={`Edit currency ${c.code}`}
                        >
                          <IconPencil />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'forex' && (
          <div className="card">
            <div className="card-header">Forex rates</div>
            <p className="settings-hint-muted settings-intro">Base currency is INR. Generate a grid for the range you need, enter rates, then save in bulk.</p>
            <div className="settings-forex-toolbar">
              <div className="settings-forex-fields">
                <div className="form-group"><label>Base currency</label>
                  <input value="INR" disabled readOnly />
                </div>
                <div className="form-group"><label>From date</label><input type="date" value={forexGrid.fromDate} onChange={e => setForexGrid(g => ({ ...g, fromDate: e.target.value }))} /></div>
                <div className="form-group"><label>To date</label><input type="date" value={forexGrid.toDate} onChange={e => setForexGrid(g => ({ ...g, toDate: e.target.value }))} /></div>
              </div>
              <div className="settings-forex-actions">
                <button type="button" className="btn btn-secondary" onClick={generateForexGrid}>Generate table</button>
              </div>
            </div>
            {forexGrid.dates.length > 0 && (
              <>
                <div className="settings-forex-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        {forexGrid.ccys.map(c => <th key={c}>1 {forexGrid.base} = ? {c}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {forexGrid.dates.map(d => (
                        <tr key={d}>
                          <td className="settings-cell-strong">{d}</td>
                          {forexGrid.ccys.map(c => (
                            <td key={c}>
                              <input type="number" step="0.0001"
                                value={forexGrid.cells[`${d}|${c}`] || ''}
                                onChange={e => updateForexCell(d, c, e.target.value)} />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4"><button type="button" className="btn btn-primary" onClick={saveForexGrid}>Save all rates</button></div>
              </>
            )}
            {forexGrid.dates.length === 0 && (
              <p className="settings-hint-muted">Choose from and to dates, then use Generate table to load or create rows.</p>
            )}
          </div>
        )}

        {tab === 'groups' && (
          <div className="settings-card-stack">
            <div className="card">
              <div className="card-header settings-card-header">
                <span>Customer groups</span>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => setCgModal({ group_name: '', description: '', is_active: 1 })}>+ Add group</button>
              </div>
              <div className="settings-table-wrap">
                <table>
                  <thead><tr><th>Group name</th><th>Description</th><th>Active</th><th>Actions</th></tr></thead>
                  <tbody>
                    {customerGroups.map(g => (
                      <tr key={g.id}>
                        <td className="settings-cell-strong">{g.group_name}</td>
                        <td>{g.description}</td>
                        <td><span className={`badge ${g.is_active ? 'badge-approved' : 'badge-draft'}`}>{g.is_active ? 'Active' : 'Inactive'}</span></td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-outline btn-sm settings-icon-btn"
                            onClick={() => setCgModal({ ...g })}
                            title="Edit customer group"
                            aria-label={`Edit group ${g.group_name}`}
                          >
                            <IconPencil />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card">
              <div className="card-header settings-card-header">
                <span>Hours change reasons</span>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => setHrsModal({ reason: '', is_active: 1 })}>+ Add reason</button>
              </div>
              <p className="settings-hint-muted settings-intro">Shown when editing worked hours on the Billing screen.</p>
              <div className="settings-table-wrap">
                <table>
                  <thead><tr><th>Reason</th><th>Active</th><th>Actions</th></tr></thead>
                  <tbody>
                    {hrsReasons.map(r => (
                      <tr key={r.id}>
                        <td className="settings-cell-strong">{r.reason}</td>
                        <td><span className={`badge ${r.is_active ? 'badge-approved' : 'badge-draft'}`}>{r.is_active ? 'Active' : 'Inactive'}</span></td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-outline btn-sm settings-icon-btn"
                            onClick={() => setHrsModal({ ...r })}
                            title="Edit reason"
                            aria-label={`Edit hours change reason ${r.reason}`}
                          >
                            <IconPencil />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card">
              <div className="card-header settings-card-header">
                <span>Key field groups</span>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => setKfgModal({ group_name: '', applies_to: 'MSA', description: '', is_active: 1 })}>+ Add key field group</button>
              </div>
              <div className="settings-table-wrap">
                <table>
                  <thead><tr><th>Group name</th><th>Class</th><th>Description</th><th>Active</th><th>Actions</th></tr></thead>
                  <tbody>
                    {keyFieldGroups.map(g => (
                      <tr key={g.id}>
                        <td className="settings-cell-strong">{g.group_name}</td>
                        <td><span className="badge badge-pending">{g.applies_to || 'MSA'}</span></td>
                        <td>{g.description}</td>
                        <td><span className={`badge ${g.is_active ? 'badge-approved' : 'badge-draft'}`}>{g.is_active ? 'Active' : 'Inactive'}</span></td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-outline btn-sm settings-icon-btn"
                            onClick={() => setKfgModal({ ...g })}
                            title="Edit key field group"
                            aria-label={`Edit key field group ${g.group_name}`}
                          >
                            <IconPencil />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === 'dashboards' && (
          <div className="card">
            <div className="card-header settings-card-header">
              <span>Dashboards</span>
              <button type="button" className="btn btn-primary btn-sm" onClick={() => setDashModal({ slug: '', name: '', description: '', is_active: 1 })}>+ Add dashboard</button>
            </div>
            <p className="settings-hint-muted settings-intro">
              Register dashboards here and assign them per user. Admins always see every active dashboard. Use Manage access to choose non-admin users.
            </p>
            <div className="settings-table-wrap">
              <table>
                <thead><tr><th>Name</th><th>Slug</th><th>Description</th><th>Active</th><th>Actions</th></tr></thead>
                <tbody>
                  {dashboards.map(d => (
                    <tr key={d.id}>
                      <td className="settings-cell-strong">{d.name}</td>
                      <td className="settings-cell-mono">{d.slug}</td>
                      <td>{d.description}</td>
                      <td><span className={`badge ${d.is_active ? 'badge-approved' : 'badge-draft'}`}>{d.is_active ? 'Active' : 'Inactive'}</span></td>
                      <td>
                        <div className="settings-table-actions" role="group" aria-label={`Actions for ${d.name}`}>
                          <button
                            type="button"
                            className="btn btn-outline btn-sm settings-icon-btn"
                            onClick={() => setDashModal({ ...d })}
                            title="Edit dashboard"
                            aria-label={`Edit dashboard ${d.name}`}
                          >
                            <IconPencil />
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline btn-sm settings-icon-btn"
                            onClick={() => openDashAccess(d)}
                            title="Manage access"
                            aria-label={`Manage access for ${d.name}`}
                          >
                            <IconPeople />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {dashboards.length === 0 && (
                    <tr><td colSpan={5} className="settings-empty-row">No dashboards registered yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'csv' && (
          <div className="card">
            <div className="card-header">CSV import</div>
            <p className="settings-hint-muted settings-intro">
              Download a template, fill it, then upload. Existing keys are updated; new rows are inserted. Invalid rows are skipped and reported in the result message.
            </p>
            <div className="settings-table-wrap">
              <table>
                <thead><tr><th>Entity</th><th>Notes</th><th>Download template</th><th>Upload CSV</th></tr></thead>
                <tbody>
                  {csvEntities.map(e => (
                    <tr key={e.key}>
                      <td className="settings-cell-strong">{e.label}</td>
                      <td className="settings-csv-note">{e.note}</td>
                      <td>
                        <div className="settings-csv-icon-cell">
                          <button
                            type="button"
                            className="btn btn-outline btn-sm settings-icon-btn"
                            onClick={() => downloadTemplate(e.key)}
                            title="Download template"
                            aria-label={`Download ${e.label} CSV template`}
                          >
                            <IconDownload />
                          </button>
                          <span className="settings-csv-icon-caption">Download template</span>
                        </div>
                      </td>
                      <td>
                        <div className="settings-csv-icon-cell">
                          <label
                            className="btn btn-primary btn-sm settings-file-trigger settings-icon-btn"
                            title="Upload CSV file"
                          >
                            <IconUpload />
                            <input
                              type="file"
                              accept=".csv"
                              aria-label={`Upload CSV file for ${e.label}`}
                              onChange={ev => { uploadCsv(e.key, ev.target.files?.[0]); ev.target.value = ''; }}
                            />
                          </label>
                          <span className="settings-csv-icon-caption">Upload CSV file</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {isHr && (
              <p className="settings-hint-muted settings-footnote-spaced">
                Future phase: HR tools may sync into Employee Master and replace this CSV path.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Signatory Modal */}
      {sigModal && (
        <div className="modal-overlay" onClick={() => setSigModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{sigModal.id ? 'Edit' : 'Add'} Signatory</h2>
            <div className="form-grid">
              <div className="form-group"><label>Name</label><input value={sigModal.name} onChange={e => setSigModal(f => ({ ...f, name: e.target.value }))} /></div>
              <div className="form-group"><label>Title</label><input value={sigModal.title} onChange={e => setSigModal(f => ({ ...f, title: e.target.value }))} /></div>
              <div className="form-group"><label>Email</label><input value={sigModal.email || ''} onChange={e => setSigModal(f => ({ ...f, email: e.target.value }))} /></div>
              {sigModal.id && <div className="form-group"><label>Active</label><select value={sigModal.is_active} onChange={e => setSigModal(f => ({ ...f, is_active: parseInt(e.target.value) }))}><option value={1}>Yes</option><option value={0}>No</option></select></div>}
            </div>
            <div className="btn-group mt-4">
              <button type="button" className="btn btn-primary" onClick={saveSig}>Save</button>
              <button type="button" className="btn btn-secondary" onClick={() => setSigModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Address Modal */}
      {addrModal && (
        <div className="modal-overlay" onClick={() => setAddrModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{addrModal.id ? 'Edit' : 'Add'} Address</h2>
            <div className="form-grid">
              <div className="form-group"><label>Title</label><input value={addrModal.address_title} onChange={e => setAddrModal(f => ({ ...f, address_title: e.target.value }))} /></div>
              <div className="form-group full-width"><label>Address Line</label><textarea value={addrModal.address_line} onChange={e => setAddrModal(f => ({ ...f, address_line: e.target.value }))} /></div>
              <div className="form-group"><label>City</label><input value={addrModal.city || ''} onChange={e => setAddrModal(f => ({ ...f, city: e.target.value }))} /></div>
              <div className="form-group"><label>State</label><input value={addrModal.state || ''} onChange={e => setAddrModal(f => ({ ...f, state: e.target.value }))} /></div>
              <div className="form-group"><label>Country</label><input value={addrModal.country || ''} onChange={e => setAddrModal(f => ({ ...f, country: e.target.value }))} /></div>
              <div className="form-group"><label>Pincode</label><input value={addrModal.pincode || ''} onChange={e => setAddrModal(f => ({ ...f, pincode: e.target.value }))} /></div>
              <div className="form-group"><label>GST / VAT No.</label><input value={addrModal.gst || ''} onChange={e => setAddrModal(f => ({ ...f, gst: e.target.value }))} placeholder="e.g. 33AADCC1234K1Z5" /></div>
              <div className="form-group"><label>Default</label><select value={addrModal.is_default ? 1 : 0} onChange={e => setAddrModal(f => ({ ...f, is_default: parseInt(e.target.value) }))}><option value={0}>No</option><option value={1}>Yes</option></select></div>
              {addrModal.id && <div className="form-group"><label>Active</label><select value={addrModal.is_active} onChange={e => setAddrModal(f => ({ ...f, is_active: parseInt(e.target.value) }))}><option value={1}>Yes</option><option value={0}>No</option></select></div>}
            </div>
            <div className="btn-group mt-4">
              <button type="button" className="btn btn-primary" onClick={saveAddr}>Save</button>
              <button type="button" className="btn btn-secondary" onClick={() => setAddrModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Currency Modal */}
      {currModal && (
        <div className="modal-overlay" onClick={() => setCurrModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{currModal._editing ? 'Edit' : 'Add'} Currency</h2>
            <div className="form-grid">
              <div className="form-group"><label>Code</label><input value={currModal.code} onChange={e => setCurrModal(f => ({ ...f, code: e.target.value.toUpperCase() }))} disabled={currModal._editing} /></div>
              <div className="form-group"><label>Name</label><input value={currModal.name} onChange={e => setCurrModal(f => ({ ...f, name: e.target.value }))} /></div>
              <div className="form-group"><label>Symbol</label><input value={currModal.symbol || ''} onChange={e => setCurrModal(f => ({ ...f, symbol: e.target.value }))} /></div>
              {currModal._editing && <div className="form-group"><label>Active</label><select value={currModal.is_active} onChange={e => setCurrModal(f => ({ ...f, is_active: parseInt(e.target.value) }))}><option value={1}>Yes</option><option value={0}>No</option></select></div>}
            </div>
            <div className="btn-group mt-4">
              <button type="button" className="btn btn-primary" onClick={saveCurr}>Save</button>
              <button type="button" className="btn btn-secondary" onClick={() => setCurrModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Group Modal */}
      {cgModal && (
        <div className="modal-overlay" onClick={() => setCgModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{cgModal.id ? 'Edit' : 'Add'} Customer Group</h2>
            <div className="form-grid">
              <div className="form-group"><label>Group Name</label><input value={cgModal.group_name} onChange={e => setCgModal(f => ({ ...f, group_name: e.target.value }))} /></div>
              <div className="form-group full-width"><label>Description</label><textarea value={cgModal.description || ''} onChange={e => setCgModal(f => ({ ...f, description: e.target.value }))} /></div>
              {cgModal.id && <div className="form-group"><label>Active</label><select value={cgModal.is_active} onChange={e => setCgModal(f => ({ ...f, is_active: parseInt(e.target.value) }))}><option value={1}>Yes</option><option value={0}>No</option></select></div>}
            </div>
            <div className="btn-group mt-4">
              <button type="button" className="btn btn-primary" onClick={saveCg}>Save</button>
              <button type="button" className="btn btn-secondary" onClick={() => setCgModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Hrs Change Reason Modal */}
      {hrsModal && (
        <div className="modal-overlay" onClick={() => setHrsModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{hrsModal.id ? 'Edit' : 'Add'} Hrs Change Reason</h2>
            <div className="form-grid">
              <div className="form-group full-width"><label>Reason</label><input value={hrsModal.reason} onChange={e => setHrsModal(f => ({ ...f, reason: e.target.value }))} /></div>
              {hrsModal.id && <div className="form-group"><label>Active</label><select value={hrsModal.is_active} onChange={e => setHrsModal(f => ({ ...f, is_active: parseInt(e.target.value) }))}><option value={1}>Yes</option><option value={0}>No</option></select></div>}
            </div>
            <div className="btn-group mt-4">
              <button type="button" className="btn btn-primary" onClick={saveHrs}>Save</button>
              <button type="button" className="btn btn-secondary" onClick={() => setHrsModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Dashboard Catalog Modal */}
      {dashModal && (
        <div className="modal-overlay" onClick={() => setDashModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{dashModal.id ? 'Edit' : 'Add'} Dashboard</h2>
            <div className="form-grid">
              <div className="form-group"><label>Name</label><input value={dashModal.name} onChange={e => setDashModal(f => ({ ...f, name: e.target.value }))} /></div>
              <div className="form-group"><label>Slug (URL key)</label><input value={dashModal.slug} onChange={e => setDashModal(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))} placeholder="e.g. billing" disabled={!!dashModal.id} /></div>
              <div className="form-group full-width"><label>Description</label><textarea value={dashModal.description || ''} onChange={e => setDashModal(f => ({ ...f, description: e.target.value }))} /></div>
              <div className="form-group"><label>Active</label><select value={dashModal.is_active} onChange={e => setDashModal(f => ({ ...f, is_active: parseInt(e.target.value) }))}><option value={1}>Yes</option><option value={0}>No</option></select></div>
            </div>
            <p className="settings-modal-footnote">The slug must match a route under <code>/dashboards/&lt;slug&gt;</code>. This form does not create the page component.</p>
            <div className="btn-group mt-4">
              <button type="button" className="btn btn-primary" onClick={saveDash}>Save</button>
              <button type="button" className="btn btn-secondary" onClick={() => setDashModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Dashboard Access Modal */}
      {dashAccessModal && (
        <div className="modal-overlay" onClick={() => setDashAccessModal(null)}>
          <div className="modal settings-dash-access-modal" onClick={e => e.stopPropagation()}>
            <h2>Manage access · {dashAccessModal.dashboard.name}</h2>
            <p className="settings-hint-muted settings-intro">Admins always see every active dashboard; their rows are disabled below.</p>
            <div className="settings-dash-access-list">
              {allUsers.filter(u => u.is_active).map(u => (
                <label key={u.id} className="settings-dash-access-row">
                  <input type="checkbox"
                    checked={dashAccessModal.userIds.has(u.id)}
                    disabled={u.role === 'Admin'}
                    onChange={() => toggleDashUser(u.id)} />
                  <span className="settings-dash-access-name">{u.full_name}</span>
                  <span className="settings-dash-access-meta">({u.username} · {u.role})</span>
                </label>
              ))}
            </div>
            <div className="btn-group mt-4">
              <button type="button" className="btn btn-primary" onClick={saveDashAccess}>Save access</button>
              <button type="button" className="btn btn-secondary" onClick={() => setDashAccessModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Key Field Group Modal */}
      {kfgModal && (
        <div className="modal-overlay" onClick={() => setKfgModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{kfgModal.id ? 'Edit' : 'Add'} Key Field Group</h2>
            <div className="form-grid">
              <div className="form-group"><label>Group Name</label><input value={kfgModal.group_name} onChange={e => setKfgModal(f => ({ ...f, group_name: e.target.value }))} /></div>
              <div className="form-group"><label>Class</label>
                <select value={kfgModal.applies_to || 'MSA'} onChange={e => setKfgModal(f => ({ ...f, applies_to: e.target.value }))}>
                  <option value="MSA">MSA</option>
                  <option value="SOW">SOW</option>
                  <option value="Both">Both</option>
                </select>
              </div>
              <div className="form-group full-width"><label>Description</label><textarea value={kfgModal.description || ''} onChange={e => setKfgModal(f => ({ ...f, description: e.target.value }))} /></div>
              {kfgModal.id && <div className="form-group"><label>Active</label><select value={kfgModal.is_active} onChange={e => setKfgModal(f => ({ ...f, is_active: parseInt(e.target.value) }))}><option value={1}>Yes</option><option value={0}>No</option></select></div>}
            </div>
            <div className="btn-group mt-4">
              <button type="button" className="btn btn-primary" onClick={saveKfg}>Save</button>
              <button type="button" className="btn btn-secondary" onClick={() => setKfgModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

    </>
  );
}
